import React, { useState, useEffect } from "react";
import { getLandlordOpportunities, generateProspects } from "../../../api";
import { ScoreBadge, ProbPill, money } from "./bits";
import OwnerIntelReport from "./OwnerIntelReport";
import { useToast } from "../../../hooks/use-toast";
import { Building2, RefreshCw, Search, Loader2, ArrowRight, MapPin } from "lucide-react";

const AdminLandlordFinder = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState("");
  const [openId, setOpenId] = useState(null);
  const [pc, setPc] = useState("");
  const [gen, setGen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getLandlordOpportunities(area ? { area } : {});
      setItems(d.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [area]);

  const findProspects = async () => {
    if (!pc.trim()) { toast({ title: "Enter a postcode or area (e.g. BA2 4)" }); return; }
    setGen(true);
    try {
      const r = await generateProspects({ area: pc.trim(), target: "lettings", count: 15 });
      toast({ title: `Found ${r.created} real prospects`, description: r.source || "HM Land Registry" });
      setPc(""); await load();
    } catch (e) {
      toast({ title: "Couldn't find prospects", description: e?.response?.data?.detail || "Try a full postcode district." });
    } finally { setGen(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-landlord-finder">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><Building2 size={20} className="text-[#b8935f]" /> AI Landlord Finder</h2>
          <p className="text-[13px] text-[#8a8a8a] mt-1">Owners ranked by landlord opportunity — probability, estimated value and the recommended next action. Figures are estimates.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="landlord-area-filter" className="text-[13px] border border-[#f0ece5] rounded-lg px-3 py-2 bg-white text-[#4a4a4a]">
            <option value="">All areas</option>
            {["BA1", "BA2", "BA3", "BS1", "BS3", "BS6", "BS8", "BS9"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={load} data-testid="landlord-refresh" className="flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border border-[#f0ece5] text-[#6a6a6a] hover:bg-[#faf7f2]"><RefreshCw size={15} /> Refresh</button>
        </div>
      </div>

      <div className="bg-[#faf7f2] border border-[#f0ece5] rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="relative flex-1">
          <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8935f]" />
          <input value={pc} onChange={(e) => setPc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && findProspects()} data-testid="landlord-postcode" placeholder="Full postcode to find real landlords — e.g. BA1 1JQ" className="w-full text-[14px] border border-[#e2d8c8] rounded-lg pl-9 pr-3 py-2.5 bg-white" />
        </div>
        <button onClick={findProspects} disabled={gen} data-testid="landlord-find" className="flex items-center justify-center gap-2 text-[14px] px-5 py-2.5 rounded-lg bg-[#b8935f] text-white font-500 hover:bg-[#a37f4e] disabled:opacity-60">{gen ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Find prospects</button>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-14 text-center bg-white border border-dashed border-[#e2d8c8] rounded-xl">
          <Building2 size={26} className="text-[#d9c4a6] mx-auto mb-3" />
          <p className="text-[14px] text-[#6a6a6a]">No prospects yet. Enter an area above and click <strong>Find prospects</strong> to pull real owners from HM Land Registry.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <button key={o.id} onClick={() => setOpenId(o.id)} data-testid={`landlord-card-${o.id}`}
              className="w-full text-left bg-white border border-[#f0ece5] rounded-xl p-4 hover:border-[#b8935f] hover:shadow-sm transition-all flex items-start gap-4">
              <ScoreBadge score={o.score} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#2b2b2b]">{o.owner}</span>
                  {o.company && <span className="text-[12px] text-[#8a8a8a]">· {o.company}</span>}
                  {o.converted && <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Converted</span>}
                </div>
                <div className="text-[12px] text-[#8a8a8a] mt-0.5">{o.address}</div>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <ProbPill label="likely landlord" value={o.pLandlord} />
                  <ProbPill label="may need management" value={o.pNeedsManagement} />
                  <span className="text-[12px] text-[#4a4a4a]">Est. <span className="font-semibold text-[#2b2b2b]">{money(o.estAnnualMgmtRevenue)}</span>/yr mgmt</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(o.signals || []).slice(0, 3).map((s, i) => <span key={i} className="text-[11px] bg-[#faf7f2] text-[#6a6a6a] rounded-full px-2 py-0.5">{s}</span>)}
                </div>
              </div>
              <div className="text-right shrink-0 hidden sm:block">
                <div className="text-[11px] text-[#b8935f] uppercase tracking-wide">Next action</div>
                <div className="text-[12px] text-[#4a4a4a] max-w-[180px] mt-0.5">{o.recommended.action}</div>
                <ArrowRight size={16} className="text-[#b8935f] ml-auto mt-2" />
              </div>
            </button>
          ))}
        </div>
      )}

      <OwnerIntelReport ownerId={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} onActioned={load} />
    </div>
  );
};

export default AdminLandlordFinder;
